import React, { useMemo } from "react";
import useControlParams from "hooks/useControlParams";
import { useGetExpenseReport } from "pages/Dashboard/dashboardQueries";
import AxiosInstance from "service/axiosInstance";
import FoodIcon from "assets/Images/statistics/food.png";
import NonFoodIcon from "assets/Images/statistics/food.png"; // fallback or icon
import LoanIcon from "assets/Images/statistics/food.png";

export default function ExpenseReportSection() {
  const { availableParams } = useControlParams();

  const reportParams = useMemo(() => {
    const raw = availableParams() || {};
    const occ = (raw.occurrence || "ALL").toUpperCase();
    let filterType = "ALL";
    if (occ === "WEEKLY") filterType = "WEEKLY";
    else if (occ === "MONTHLY") filterType = "MONTHLY";
    else if (occ === "DAILY" || occ === "CUSTOM") filterType = "CUSTOM";

    return {
      filterType,
      year: raw.year || new Date().getFullYear(),
      month: raw.month ? Number(raw.month) : new Date().getMonth() + 1,
      ...(raw.start && { start: raw.start }),
      ...(raw.end && { end: raw.end }),
    };
  }, [availableParams]);

  const { data: expenseData, isLoading } = useGetExpenseReport(reportParams);

  const reportData = expenseData?.data || {};

  const handleDownload = async (format) => {
    try {
      const search = new URLSearchParams({
        ...reportParams,
        reportType: "expense",
      }).toString();
      const endpoint = format === "pdf" ? "download-pdf" : "download";
      const downloadUrl = `/api/v1/transaction-report/${endpoint}?${search}`;

      const response = await AxiosInstance({
        url: downloadUrl,
        method: "GET",
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type:
          format === "pdf"
            ? "application/pdf"
            : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `expense-report-${Date.now()}.${format === "pdf" ? "pdf" : "xlsx"}`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Failed to download expense report:", err);
    }
  };

  const sectionsConfig = [
    {
      key: "food",
      title: "Food",
      data: reportData?.food || reportData?.sections?.find((s) => s.key === "food"),
      badgeBg: "bg-emerald-50 text-emerald-700 border-emerald-200",
      barColor: "bg-emerald-500",
      accentHex: "#10B981",
    },
    {
      key: "nonFood",
      title: "Non-food",
      data: reportData?.nonFood || reportData?.sections?.find((s) => s.key === "nonFood"),
      badgeBg: "bg-purple-50 text-purple-700 border-purple-200",
      barColor: "bg-purple-500",
      accentHex: "#8B5CF6",
    },
    {
      key: "loanRepayments",
      title: "Loan repayments",
      data: reportData?.loanRepayments || reportData?.sections?.find((s) => s.key === "loanRepayments"),
      badgeBg: "bg-blue-50 text-blue-700 border-blue-200",
      barColor: "bg-blue-500",
      accentHex: "#3B82F6",
    },
  ];

  return (
    <div className="w-full bg-[#FFFFFF99] backdrop-blur-md rounded-2xl p-6 border border-white shadow-sm flex flex-col gap-6">
      {/* Header & Download Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-slate-200/60">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 inline-block"></span>
            Expense Report
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Top 3 most expensive sub-categories by expenditure category
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleDownload("excel")}
            className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-300/80 rounded-xl transition-all shadow-sm active:scale-95 cursor-pointer"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Excel Download
          </button>

          <button
            onClick={() => handleDownload("pdf")}
            className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-300/80 rounded-xl transition-all shadow-sm active:scale-95 cursor-pointer"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2zM9 9h6m-6 4h6m-6 4h4"
              />
            </svg>
            PDF Download
          </button>
        </div>
      </div>

      {/* Loading Skeleton */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-pulse">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-48 bg-slate-200/60 rounded-xl"></div>
          ))}
        </div>
      ) : (
        /* 3 Sections Grid */
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {sectionsConfig.map((config) => {
            const sec = config.data || {};
            const subCats = sec.topSubCategories || [];
            const total = sec.totalAmount || 0;

            return (
              <div
                key={config.key}
                className="bg-white/80 rounded-xl p-5 border border-slate-200/80 shadow-sm flex flex-col justify-between hover:shadow-md transition-shadow"
              >
                <div>
                  {/* Category Header */}
                  <div className="flex items-center justify-between mb-4">
                    <span
                      className={`text-xs font-semibold px-3 py-1 rounded-full border ${config.badgeBg}`}
                    >
                      {config.title}
                    </span>
                    <span className="text-base font-bold text-slate-800">
                      ₹{total.toLocaleString()}
                    </span>
                  </div>

                  {/* Subcategories list */}
                  <div className="space-y-4">
                    {subCats.length === 0 ? (
                      <p className="text-xs text-slate-400 py-6 text-center italic">
                        No transactions recorded
                      </p>
                    ) : (
                      subCats.map((sub, idx) => {
                        const pct = sub.percentage || 0;
                        return (
                          <div key={idx} className="flex flex-col gap-1.5">
                            <div className="flex items-center justify-between text-xs">
                              <span className="font-medium text-slate-700 truncate max-w-[70%]">
                                {sub.name}
                              </span>
                              <span className="font-bold text-slate-900">
                                ₹{sub.amount?.toLocaleString()}{" "}
                                <span className="text-[10px] text-slate-400 font-normal">
                                  ({pct}%)
                                </span>
                              </span>
                            </div>

                            {/* Progress bar container */}
                            <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden relative">
                              <div
                                className={`h-full ${config.barColor} rounded-full transition-all duration-500`}
                                style={{ width: `${Math.min(pct, 100)}%` }}
                              />
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Total Summary Footer */}
                <div className="mt-6 pt-3 border-t border-slate-100 flex justify-between items-center text-xs text-slate-500">
                  <span>Top 3 Sub-categories</span>
                  <span className="font-semibold text-slate-700">
                    {subCats.length} items
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
